import { computed, inject, Injectable } from '@angular/core';
import { ALREADY_SPINED_PROMO, BasePromo, EventType } from '../base-promo';
import { TournamentsService } from '../../tournaments/tournaments.service';
import { GameListConfig } from '../../../core/shared/components/game-list/game-list.component';
import { GameCategory } from '../../../core/services/games/game-category';
import { useStore } from 'ngx-unificator/store';
import { GlobalStoreName } from '../../../core/store';
import { SummerPageType, SummerStoreEntity } from '../../../core/store/summer.store';
import { concatMap, filter, map, switchMap, tap } from 'rxjs/operators';
import { combineLatest, Observable, of } from 'rxjs';
import { ITournament } from '../../tournaments/tournaments.interface';
import { BonusStatus } from '../../../core/services/user/data/user-bonuses.data';
import { ALL_WELCOME_CLAIMED } from '../../../core/services/user/data/user-group.data';

const BONUS_IDS: string[] = [
  'promo_spins_1_sp25',
  'promo_spins_2_sp25',
  'promo_spins_3_sp25',
];

export enum SummerAction {
  GO_TO_DEPOSIT = 'deposit',
  TOURNAMENTS = 'tournaments',
  PROMO_AUTH = 'promo-auth',
  GET_BONUS = 'get-bonus',
  GO_TO_BONUSES = 'bonuses',
  GO_TO_TELEGRAM = 'telegram',
}

@Injectable({
  providedIn: 'root',
})
export class SummerService extends BasePromo<SummerPageType> {

  /**
   * Event name
   */
  public eventName = 'summer';

  /**
   * Services
   */
  public tournaments: TournamentsService = inject(TournamentsService);

  /**
   * Tournaments list
   */
  public tournaments$: Observable<ITournament[]> = this._getTournaments$();

  /**
   * Summer promo store
   */
  public summerStore = useStore<SummerStoreEntity>(GlobalStoreName.SUMMER_STORE);

  public slots = computed(() => this.summerStore.storeSignal().slots);
  public store = computed(() => this.summerStore.storeSignal());
  public isUserCanPartiallyParticipateInPromo = computed(() => this.store().isUserCanPartiallyParticipateInPromo);
  public isUserCanParticipateInPromo = computed(() => this.store().isUserCanParticipateInPromo);
  public promoIssuedId = computed(() => this.store().currentBonus?.promoIssuedLootbox?.id);

  /**
   * Game list config
   */
  public gamesListConfig: GameListConfig = {
    filter: {
      category: GameCategory.SUMMER,
      limit: 5,
    },
    link: '/games/summerhits',
    title: 'link.summer-favorites',
    hideTitle: true,
  };

  /**
   * Action rotes config
   */
  public actionRoutesConfig = {
    [SummerAction.GET_BONUS]: {
      url: '/account',
      isNeedRedirect: false,
      isAnchorUrl: false,
    },
    [SummerAction.GO_TO_BONUSES]: {
      url: '/bonuses',
      isNeedRedirect: false,
      isAnchorUrl: true,
    },
    [SummerAction.TOURNAMENTS]: {
      url: '/tournaments',
      isNeedRedirect: true,
      isAnchorUrl: false,
    },
    [SummerAction.GO_TO_DEPOSIT]: {
      url: '/account/deposit',
      isNeedRedirect: true,
      isAnchorUrl: false,
    },
    [SummerAction.PROMO_AUTH]: {
      url: this.cookie.check('registered') ? '/login' : '/register',
      isNeedRedirect: true,
      isAnchorUrl: false,
    },
    [SummerAction.GO_TO_TELEGRAM]: {
      url: 'https://t.me/AllStarCasinoOfficial',
      isNeedRedirect: false,
      isNativeUrl: true,
      isAnchorUrl: false,
    },
  };

  constructor() {
    super();
  }

  /**
   * Init promotion and reduce store items
   */
  public initPromo$() {
    this._updateStoreEntities({ isLoadedPromo: false });
    return this.fetchPromoData$().pipe(
      filter(data => !!data[0]),
      tap(([promo, lootboxes, bonuses]) => {
        this._updatePageStore(promo);
        this._updateCurrentBonus(lootboxes, bonuses);
        this._updateParticipateInPromo(lootboxes);
        this._updateStoreEntities({ isLoadedPromo: true });
        this.initWelcomePromoModal();
      }),
    );
  }

  public onActivateBonus$(): Observable<boolean> {

    return combineLatest([
      ...this.loadNecessaryBonuses$(),
    ]).pipe(
      switchMap(([lootboxes, bonuses]) => {
        this._updateCurrentBonus(lootboxes, bonuses);
        this._updateParticipateInPromo(lootboxes);

        if (this.isUserCanPartiallyParticipateInPromo()) {
          this._handlePartialParticipationPrizeUpdate();
          return of(true);
        }

        if (this.isUserCanParticipateInPromo() && this.promoIssuedId()) {
          return this._activateLootboxAndHandlePrize(this.promoIssuedId());
        }

        return of(true);
      }),
    );
  }

  private _handlePartialParticipationPrizeUpdate(): void {
    const { fs, cash } = this.store().currentBonus || {};

    this._updateStoreEntities({
      currentPrize: {
        fs_count: fs?.issuedRounds,
        cash: cash ? `${cash?.balance} ${cash.currency}` : undefined,
      },
    });

    this.storage.set(ALREADY_SPINED_PROMO, '1');
  }

  private _activateLootboxAndHandlePrize(lootboxId: string): Observable<boolean> {
    return this.lootboxService.activateLootBox(lootboxId).pipe(
      switchMap((response) => {
        const bonus = response?.data?.result?.data?.bonus;

        if ('betNumber' in bonus?.settings) {
          this._updateStoreEntities({
            currentPrize: {
              fs_count: bonus.settings.betNumber,
            },
          });
        }

        this.storage.set(ALREADY_SPINED_PROMO, '1');

        return of(true);
      }),
    );
  }

  /**
   * Update page store
   * @param promo
   * @private
   */
  private _updatePageStore(promo: any[]): any {
    const prizes = [
      ...Object.values(promo[0]?.Prizes).map((prize: any) => ({
        ...prize,
        fs: Number(prize.fs),
        image: `/assets/img/promo/summer/prizes/${Number(prize.image)}.png`,
      })),
    ];

    const page = {
      ...promo?.map(promoItem => ({
        ...promoItem,
        HowItWorksSteps: [...Object.values(promoItem?.HowItWorksSteps)],
        unpublishAt: this.time.toLocalDate(promoItem?.unpublishAt),
      }))[0],
    };

    const slots = {
      prizes,
      initialPrizes: prizes.slice(0, 3),
    };

    this._updateStoreEntities({ page, slots });

  };

  public clearPrizeStore() {
    this._updateStoreEntities({
      currentPrize: null,
    });
  }

  public updateStoreEntities(entity: Partial<SummerStoreEntity>) {
    this._updateStoreEntities(entity);
  }

  /**
   * Update current bonus store
   * @param lootboxes
   * @param bonuses
   * @private
   */
  private _updateCurrentBonus(lootboxes: any, bonuses: any) {

    const promoIssuedLootbox = this.getFilteredPromoLootbox(lootboxes, BONUS_IDS, BonusStatus.NEW);
    const cash = this.getFilteredCashWelcomeBonuses(bonuses);
    const fs = this.getFilteredFreespinsWelcomeBonuses(bonuses);

    this._updateStoreEntities({
      currentBonus: fs && cash || promoIssuedLootbox ? {
        promoIssuedLootbox,
        fs,
        cash,
      } : null,
    });
  }

  /**
   * Update participating in promo store items
   * @private
   */
  private _updateParticipateInPromo(lootboxes: any[]) {

    const promoIssuedLootbox = this.store().currentBonus?.promoIssuedLootbox;
    const promoActivatedLootbox = this.getFilteredPromoLootbox(lootboxes, BONUS_IDS, BonusStatus.OPENED);
    const cash = this.store().currentBonus?.cash;
    const fs = this.store().currentBonus?.fs;

    const isUserAlreadyActivatedPromoLootbox = this.checkActivatedTodayLootbox(promoActivatedLootbox, BONUS_IDS);
    const isAllWelcomePackClaimed = this.group.isExistGroup(ALL_WELCOME_CLAIMED);
    const isUserCanParticipateInPromo = Boolean(isAllWelcomePackClaimed && promoIssuedLootbox);
    const isUserCanPartiallyParticipateInPromo = Boolean((cash && fs) && !promoIssuedLootbox);

    this._updateStoreEntities({
      isUserCanParticipateInPromo,
      isUserCanPartiallyParticipateInPromo,
      isAllWelcomePackClaimed,
      isUserAlreadyActivatedPromoLootbox,
    });
  }

  private _updateStoreEntities(entity: Partial<SummerStoreEntity>) {
    this.summerStore.storeSignal.update(store => {
      return {
        ...store,
        ...entity,
      };
    });
  }

  /**
   * Get promo tournaments
   * @private
   */
  private _getTournaments$(): Observable<any> {
    return this.tournaments.list({ with_game_list: 1 }).pipe(
      filter((tournaments) => !!tournaments),
      map(tournaments => tournaments.now.filter(tournament => {
        return tournament.slug.includes('easter');
      })),
    );
  }

  async checkActivatedBonuses() {
    const component = await import('../summer/summer-welcome-modal/summer-welcome-modal.component');
    if (!this.store().isUserAlreadyActivatedPromoLootbox) {
      await this.modals.openLazy(component?.SummerWelcomeModalComponent);
      const now = new Date();
      const day = now.getDay();
      const diff = 7 - day;
      const endOfWeek = new Date(now);
      endOfWeek.setDate(now.getDate() + diff);
      endOfWeek.setHours(23, 59, 59, 999);
      this.cookie.set('welcome-modal-promo', 'true', endOfWeek, '/');
    }
    return of(true);
  }

  openHuntModal(type: EventType) {
  }
}
